import { ChevronDown, Loader2, Mic, Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  gravacoesConsultaControllerUrlDoTrecho,
  useGravacoesConsultaControllerObter,
} from '@/api/generated/gravações-de-consulta/gravações-de-consulta';
import { ApiError } from '@/api/http-client';
import { formatarDuracao } from '@/shared/hooks/useGravadorDeAudio';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { toast } from '@/shared/ui/sonner';

interface Props {
  gravacaoId: string;
  duracaoSegundos: number;
}

/**
 * A consulta gravada de um prontuário, no histórico: selo sempre visível;
 * player e transcrição só ao abrir (a gravação e as URLs só são buscadas aí).
 *
 * O áudio está em trechos de ~5 min; o player toca um depois do outro e mostra
 * o tempo da consulta inteira. A barra é só leitura: pular dentro de trechos
 * WebM sem duração no cabeçalho é pouco confiável no navegador.
 */
export function ConsultaGravada({ gravacaoId, duracaoSegundos }: Props) {
  const [aberto, setAberto] = useState(false);
  const { data: gravacao, isPending } = useGravacoesConsultaControllerObter(gravacaoId, {
    query: { enabled: aberto },
  });

  const audioRef = useRef<HTMLAudioElement>(null);
  const [indice, setIndice] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [tocando, setTocando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [posicaoNoTrecho, setPosicaoNoTrecho] = useState(0);
  const tocarAoCarregar = useRef(false);

  const trechos = gravacao?.trechos ?? [];
  const antesDoTrecho = trechos.slice(0, indice).reduce((soma, trecho) => soma + trecho.duracaoSegundos, 0);
  const total = gravacao?.duracaoSegundos ?? duracaoSegundos;

  useEffect(() => {
    if (url && tocarAoCarregar.current) {
      tocarAoCarregar.current = false;
      void audioRef.current?.play().catch(() => setTocando(false));
    }
  }, [url]);

  async function carregarTrecho(novoIndice: number) {
    const trecho = trechos[novoIndice];
    if (!trecho) return;
    setCarregando(true);
    try {
      const arquivo = await gravacoesConsultaControllerUrlDoTrecho(gravacaoId, trecho.ordem);
      setIndice(novoIndice);
      setPosicaoNoTrecho(0);
      tocarAoCarregar.current = true;
      setUrl(arquivo.url);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível carregar o áudio.');
    } finally {
      setCarregando(false);
    }
  }

  function alternar() {
    if (tocando) {
      audioRef.current?.pause();
    } else if (url) {
      void audioRef.current?.play().catch(() => setTocando(false));
    } else {
      void carregarTrecho(0);
    }
  }

  return (
    <Collapsible open={aberto} onOpenChange={setAberto} className="rounded-md border border-brand/15 bg-brand/5 px-3 py-2">
      <CollapsibleTrigger className="group flex w-full items-center gap-1.5 text-xs font-medium text-brand">
        <Mic className="w-3.5 h-3.5" />
        Consulta gravada · {formatarDuracao(duracaoSegundos)}
        <ChevronDown className="ml-auto w-3.5 h-3.5 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-2 pt-2">
        {isPending ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando a gravação...
          </p>
        ) : !gravacao ? (
          <p className="text-xs text-destructive">Não foi possível carregar a gravação.</p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={alternar}
                disabled={carregando || trechos.length === 0}
                aria-label={tocando ? 'Pausar consulta gravada' : 'Ouvir consulta gravada'}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground hover:bg-brand/90 disabled:opacity-60"
              >
                {carregando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : tocando ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4 translate-x-px" />
                )}
              </button>
              <div
                role="progressbar"
                aria-label="Andamento da consulta gravada"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={Math.round(antesDoTrecho + posicaoNoTrecho)}
                className="h-1 flex-1 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full bg-brand"
                  style={{ width: `${Math.min(100, ((antesDoTrecho + posicaoNoTrecho) / Math.max(1, total)) * 100)}%` }}
                />
              </div>
              <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {formatarDuracao(antesDoTrecho + posicaoNoTrecho)} / {formatarDuracao(total)}
              </span>
              {url && (
                <audio
                  ref={audioRef}
                  src={url}
                  preload="auto"
                  onPlay={() => setTocando(true)}
                  onPause={() => setTocando(false)}
                  onTimeUpdate={(e) => setPosicaoNoTrecho(e.currentTarget.currentTime)}
                  onEnded={() => {
                    // Próximo trecho, sem clique — a consulta toca inteira.
                    if (indice + 1 < trechos.length) void carregarTrecho(indice + 1);
                    else {
                      setTocando(false);
                      setUrl(null);
                      setIndice(0);
                      setPosicaoNoTrecho(0);
                    }
                  }}
                  onError={() => {
                    setTocando(false);
                    setUrl(null);
                  }}
                />
              )}
            </div>

            <p className="text-[11px] text-muted-foreground">
              Gravada por {gravacao.criadoPorNome} · consentimento do tutor confirmado em{' '}
              {new Date(gravacao.consentimentoEm).toLocaleString('pt-BR')}
            </p>

            {gravacao.transcricao ? (
              <Collapsible>
                <CollapsibleTrigger className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
                  <ChevronDown className="w-3 h-3 transition-transform group-data-[state=open]:rotate-180" />
                  Ver transcrição
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <p className="mt-1 max-h-60 overflow-y-auto rounded-md bg-background/60 px-2.5 py-1.5 text-xs text-muted-foreground whitespace-pre-wrap">
                    {gravacao.transcricao}
                  </p>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <p className="text-xs text-muted-foreground">Sem transcrição.</p>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
